/**
 * ExpedienteDetailScreen — route `/rounds/:roundId/review/:expedienteId`
 * (reviewer+). The reviewer's workbench: verify the Capa 0 extraction against
 * the actual documents, then issue the dictamen.
 *
 * Layout: queue nav + title → full-width VerdictBanner (the effective
 * disposition as an official stamp) → grid: zoomable DocumentWorkbench (left,
 * ~60%) and a sticky rail (right) with extracted data, findings, confidence,
 * trace metadata, and the adjudication form.
 *
 * Cold deep-link: fetches its own detail (`getExpedienteDetail`) and document
 * URLs (`getExpedienteDocuments`) — no in-memory queue is assumed. When the
 * Tablero browser handed a frozen queue snapshot via `location.state`, the
 * header shows "N de M" prev/next navigation over that snapshot (see
 * detail/queueState.ts for why it never refetches).
 *
 * Write-path rule: the reviewer identity is injected SERVER-SIDE — it is NEVER
 * put in the request body. On 201 the detail reloads (the effective
 * disposition flips to ADJUDICATED).
 */

import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";

import { api, ApiError, useApiQuery } from "../api/useApi";
import { IS_DEMO, type DocumentPackageResponse, type DocumentRef, type ExpedienteDetail } from "../api/client";
import { Loading } from "../components/Loading";
import { ProblemAlert } from "../components/ProblemAlert";
import { EmptyState } from "../components/EmptyState";

import { QueueNav } from "./detail/QueueNav";
import { isReviewQueueState, type ReviewQueueState } from "./detail/queueState";
import { VerdictBanner } from "./detail/VerdictBanner";
import { DocumentWorkbench } from "./detail/DocumentWorkbench";
import { ExtractedDataCard } from "./detail/ExtractedDataCard";
import { FindingsCard } from "./detail/FindingsCard";
import { ConfidencePanel } from "./detail/ConfidencePanel";
import { QrCard } from "./detail/QrCard";
import { MetadataFooter } from "./detail/MetadataFooter";
import { AdjudicationForm } from "./detail/AdjudicationForm";
import { demoExpedienteChecks, demoExpedienteDetail, demoExpedienteDocuments } from "./detail/demoDetail";
import { isSingleIdentificationSource } from "./detail/identification";

export default function ExpedienteDetailScreen() {
  const { roundId, expedienteId } = useParams<{ roundId: string; expedienteId: string }>();
  const location = useLocation();
  const [packageState, setPackageState] = useState<DocumentPackageResponse | null>(null);
  const [packageError, setPackageError] = useState<string | null>(null);
  const [packageBusy, setPackageBusy] = useState(false);

  // The browser's frozen queue snapshot — untyped on the wire, so validate the
  // shape and the round before trusting it. Cold deep-links carry none.
  const rawQueue = (location.state as { queue?: unknown } | null)?.queue;
  const queue: ReviewQueueState | null =
    isReviewQueueState(rawQueue) && rawQueue.roundId === roundId ? rawQueue : null;

  const detailQuery = useApiQuery<ExpedienteDetail | null>(async () => {
    if (IS_DEMO) return demoExpedienteDetail(expedienteId!);
    const res = await api.GET("/rounds/{round_id}/expedientes/{expediente_id}", {
      params: { path: { round_id: roundId!, expediente_id: expedienteId! } },
    });
    return res.data!;
  }, [roundId, expedienteId]);

  const docsQuery = useApiQuery<DocumentRef[]>(async () => {
    // DEMO: synthetic inline documents for the expedientes that exercise a
    // document-level case (#275); the rest keep the workbench empty state.
    if (IS_DEMO) return demoExpedienteDocuments(expedienteId!);
    // Round-scoped: the images shown must belong to THIS round's record (F12).
    const res = await api.GET("/rounds/{round_id}/expedientes/{expediente_id}/documents", {
      params: { path: { round_id: roundId!, expediente_id: expedienteId! } },
    });
    return res.data!.documents;
  }, [roundId, expedienteId]);

  // The QR official-URL classification (issue #261) lives in the latest run's
  // schemaless `checks` (`layer_summary`), which only the history endpoint
  // serves — the detail payload has no checks. A fetch error renders as the
  // QrCard's own neutral "no se pudo consultar" line (never "sin QR", which
  // would misstate a fact about the document); it never blocks the review.
  const checksQuery = useApiQuery<unknown>(async () => {
    if (IS_DEMO) return demoExpedienteChecks(expedienteId!);
    const res = await api.GET("/rounds/{round_id}/expedientes/{expediente_id}/history", {
      params: { path: { round_id: roundId!, expediente_id: expedienteId! } },
    });
    const runs = res.data!.runs;
    return runs.length > 0 ? runs[runs.length - 1].checks : null;
  }, [roundId, expedienteId]);

  useEffect(() => {
    if (!packageState || !roundId || !expedienteId) return;
    if (packageState.state !== "QUEUED" && packageState.state !== "RUNNING") return;
    const timer = window.setTimeout(() => {
      void api.GET("/document-packages/{package_id}", {
        params: { path: { package_id: packageState.package_id } },
      }).then((res) => setPackageState(res.data!)).catch(() => undefined);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [packageState, roundId, expedienteId]);

  const requestPackage = async () => {
    if (!roundId || !expedienteId) return;
    setPackageBusy(true);
    setPackageError(null);
    try {
      if (IS_DEMO) {
        const blob = new Blob(["Vera DEMO package — synthetic documents only\n"], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `expediente-${expedienteId}.zip`;
        link.click();
        URL.revokeObjectURL(url);
        return;
      }
      const res = await api.POST("/rounds/{round_id}/expedientes/{expediente_id}/document-packages", {
        params: { path: { round_id: roundId, expediente_id: expedienteId } },
      });
      setPackageState(res.data!);
    } catch (err) {
      setPackageError(err instanceof ApiError ? err.detail ?? err.title ?? "No se pudo solicitar el paquete." : "No se pudo solicitar el paquete.");
    } finally {
      setPackageBusy(false);
    }
  };

  const downloadPackage = async () => {
    if (!packageState) return;
    setPackageBusy(true);
    try {
      const res = await api.POST("/document-packages/{package_id}/download-links", {
        params: { path: { package_id: packageState.package_id } },
      });
      window.location.assign(res.data!.url);
    } catch (err) {
      setPackageError(err instanceof ApiError ? err.detail ?? err.title ?? "No se pudo emitir el enlace." : "No se pudo emitir el enlace.");
    } finally {
      setPackageBusy(false);
    }
  };

  // Missing route params → defensive (the router guards normally prevent this).
  if (!roundId || !expedienteId) {
    return (
      <EmptyState
        title="Expediente no especificado"
        description="Falta el identificador de la ronda o del expediente en la dirección."
      />
    );
  }

  const header = (
    <Box sx={{ mb: 2 }}>
      <QueueNav roundId={roundId} queue={queue} />
      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1, alignItems: "baseline", flexWrap: "wrap" }}
        useFlexGap
      >
        <Typography variant="h1">Expediente {expedienteId}</Typography>
        <Typography variant="body2" color="text.secondary">
          Ronda {roundId}
        </Typography>
        <Button size="small" variant="outlined" onClick={packageState?.state === "READY" ? downloadPackage : requestPackage} disabled={packageBusy}>
          {packageState?.state === "READY" ? "Descargar documentos" : "Preparar documentos"}
        </Button>
      </Stack>
      {packageState && packageState.state !== "READY" && (
        <Box sx={{ mt: 1, maxWidth: 280 }}>
          <LinearProgress variant="determinate" value={packageState.bytes_total ? (packageState.bytes_processed / packageState.bytes_total) * 100 : 0} />
          <Typography variant="caption" color="text.secondary">{packageState.state}: {packageState.files_processed}/{packageState.files_total} archivos</Typography>
        </Box>
      )}
      {packageError && <Typography variant="caption" color="error">{packageError}</Typography>}
    </Box>
  );

  if (detailQuery.loading) {
    return (
      <Box>
        {header}
        <Loading label="Cargando expediente…" />
      </Box>
    );
  }

  if (detailQuery.error) {
    // 401/403 are handled by the route guards; pass everything else to ProblemAlert.
    return (
      <Box>
        {header}
        <ProblemAlert error={detailQuery.error} onRetry={detailQuery.reload} />
      </Box>
    );
  }

  const detail = detailQuery.data;
  if (!detail) {
    return (
      <Box>
        {header}
        <EmptyState title="Expediente no encontrado" />
      </Box>
    );
  }

  // ADR 0007 / #275: derived from the documents listing (digest equality); the
  // findings card uses it to phrase the INE-pending motivo in domain terms.
  const singleIdentification = isSingleIdentificationSource(docsQuery.data);

  const needsHumanReview =
    detail.effective_disposition.source !== "ADJUDICATED" &&
    detail.effective_disposition.decision === "HUMAN_REVIEW";

  return (
    <Box>
      {header}

      <VerdictBanner detail={detail} />

      <Box
        sx={{
          display: "grid",
          gap: 3,
          // Workbench 60 / rail 40; minmax(0, …) keeps wide images from
          // blowing the track.
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" },
          alignItems: "start",
        }}
      >
        {/* Left: the document workbench, pinned at lg so the evidence stays in
            view while the rail (and page) scroll. The stage height is
            viewport-derived (see DocumentStage) so the whole card fits under
            the sticky topbar. Below lg it flows in DOM (= mobile) order. */}
        <Box sx={{ position: { lg: "sticky" }, top: { lg: 76 } }}>
          <DocumentWorkbench
            documents={docsQuery.data}
            loading={docsQuery.loading}
            error={docsQuery.error}
            onRetry={docsQuery.reload}
          />
        </Box>

        {/* Right: the rail — evidence, findings, then the dictamen. Plain flow
            (no own scrollbar): cards keep their natural height. When the
            expediente is pending human review, Findings + Confidence lead
            the rail so the reviewer sees why it landed there immediately. */}
        <Stack spacing={2.5}>
          {needsHumanReview ? (
            <>
              <FindingsCard automated={detail.automated} singleIdentification={singleIdentification} />
              <ConfidencePanel summary={detail.automated.confidence_summary} />
              <ExtractedDataCard fields={detail.automated.extracted_fields} />
            </>
          ) : (
            <>
              <ExtractedDataCard fields={detail.automated.extracted_fields} />
              <FindingsCard automated={detail.automated} singleIdentification={singleIdentification} />
              <ConfidencePanel summary={detail.automated.confidence_summary} />
            </>
          )}
          <QrCard
            checks={checksQuery.data}
            loading={checksQuery.loading}
            unavailable={checksQuery.error !== null}
          />
          <MetadataFooter automated={detail.automated} />
          <AdjudicationForm
            detail={detail}
            roundId={roundId}
            expedienteId={expedienteId}
            onAdjudicated={detailQuery.reload}
          />
        </Stack>
      </Box>
    </Box>
  );
}
